'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

// Platform Input + Select — semantic-token-driven, sharp-cornered by
// default. Enforces a11y at compile time: exactly one of `label` or
// `srLabel` is required (so no input can ship without an accessible name).

const fieldVariants = cva(
  [
    'block bg-background text-foreground caret-primary',
    // --control-border, not --border. A form-control edge needs 3:1 against
    // its fill (WCAG 1.4.11); on the Radar dashboard surface --border is a
    // ~1.2:1 hairline meant for cards and dividers. --control-border stays
    // opaque in every scope, so this is the only edge token safe here.
    'border border-[var(--control-border)]',
    'placeholder:text-muted-foreground',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
    'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'aria-[invalid=true]:border-[var(--destructive)] aria-[invalid=true]:focus-visible:ring-[var(--destructive)]',
  ].join(' '),
  {
    variants: {
      inputSize: {
        xs: 'h-7 text-[11px]',
        sm: 'h-8 text-xs',
        md: 'h-9 text-xs',
        lg: 'h-10 text-sm',
      },
      rounded: {
        none: 'rounded-none',
        sm: 'rounded-sm',
        md: 'rounded-md',
      },
    },
    defaultVariants: {
      inputSize: 'md',
      rounded: 'none',
    },
  }
)

// Padding presets that line up with inputSize + presence of leading/trailing
// icons. Keeps icon-aware spacing in one place so consumers don't drift.
const paddingMap: Record<
  NonNullable<VariantProps<typeof fieldVariants>['inputSize']>,
  { base: string; leading: string; trailing: string }
> = {
  xs: { base: 'px-2', leading: 'pl-7', trailing: 'pr-7' },
  sm: { base: 'px-2.5', leading: 'pl-8', trailing: 'pr-8' },
  md: { base: 'px-3', leading: 'pl-9', trailing: 'pr-9' },
  lg: { base: 'px-3', leading: 'pl-9', trailing: 'pr-9' },
}

const iconSizeMap: Record<
  NonNullable<VariantProps<typeof fieldVariants>['inputSize']>,
  number
> = { xs: 12, sm: 14, md: 14, lg: 16 }

const iconWrapperMap: Record<
  NonNullable<VariantProps<typeof fieldVariants>['inputSize']>,
  { leading: string; trailing: string }
> = {
  xs: { leading: 'left-2', trailing: 'right-2' },
  sm: { leading: 'left-2.5', trailing: 'right-2.5' },
  md: { leading: 'left-3', trailing: 'right-3' },
  lg: { leading: 'left-3', trailing: 'right-3' },
}

type IconInput =
  | React.ComponentType<{ size?: number; className?: string }>
  | React.ReactNode

function renderIcon(icon: IconInput | undefined, size: number) {
  if (!icon) return null
  // Lucide icons are forwardRef objects (`typeof === 'object'`), not
  // functions — discriminate via React.isValidElement instead.
  if (React.isValidElement(icon)) {
    return icon
  }
  const IconComponent = icon as React.ComponentType<{
    size?: number
    className?: string
  }>
  return (
    <IconComponent size={size} className='text-muted-foreground shrink-0' />
  )
}

// Discriminated union — exactly one of label OR srLabel must be supplied.
// This is the a11y compile-time gate: no input can ship without an
// accessible name.
type FieldLabelProps =
  | { label: string; srLabel?: never }
  | { label?: never; srLabel: string }

type BaseFieldProps = {
  helpText?: string
  error?: string | null
  inputSize?: VariantProps<typeof fieldVariants>['inputSize']
  rounded?: VariantProps<typeof fieldVariants>['rounded']
  containerClassName?: string
}

export type InputProps = FieldLabelProps &
  BaseFieldProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
    leadingIcon?: IconInput
    trailingIcon?: IconInput
    loading?: boolean
  }

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      srLabel,
      helpText,
      error,
      inputSize = 'md',
      rounded = 'none',
      leadingIcon,
      trailingIcon,
      loading = false,
      id,
      containerClassName,
      className,
      ...inputProps
    },
    ref
  ) => {
    const reactId = React.useId()
    const fieldId = id ?? `input-${reactId}`
    const helpId = helpText ? `${fieldId}-help` : undefined
    const errorId = error ? `${fieldId}-error` : undefined
    const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined

    const padding = paddingMap[inputSize ?? 'md']
    const iconSize = iconSizeMap[inputSize ?? 'md']
    const iconWrappers = iconWrapperMap[inputSize ?? 'md']

    const effectiveTrailingIcon = loading
      ? ((
          <Loader2 size={iconSize} className='animate-spin text-primary' />
        ) as React.ReactNode)
      : trailingIcon

    const paddingClass = cn(
      padding.base,
      leadingIcon && padding.leading,
      effectiveTrailingIcon && padding.trailing
    )

    return (
      <div className={cn('w-full', containerClassName)}>
        {label ? (
          <label
            htmlFor={fieldId}
            className='block text-xs font-medium text-foreground mb-1.5'
          >
            {label}
          </label>
        ) : (
          <label htmlFor={fieldId} className='sr-only'>
            {srLabel}
          </label>
        )}

        <div className='relative w-full'>
          {leadingIcon && (
            <span
              className={cn(
                'absolute top-1/2 -translate-y-1/2 flex items-center pointer-events-none',
                iconWrappers.leading
              )}
            >
              {renderIcon(leadingIcon, iconSize)}
            </span>
          )}
          <input
            ref={ref}
            id={fieldId}
            aria-invalid={!!error || undefined}
            aria-describedby={describedBy}
            className={cn(
              fieldVariants({ inputSize, rounded }),
              'w-full',
              paddingClass,
              className
            )}
            {...inputProps}
          />
          {effectiveTrailingIcon && (
            <span
              className={cn(
                'absolute top-1/2 -translate-y-1/2 flex items-center pointer-events-none',
                iconWrappers.trailing
              )}
            >
              {renderIcon(effectiveTrailingIcon, iconSize)}
            </span>
          )}
        </div>

        {error ? (
          <p
            id={errorId}
            role='alert'
            className='mt-1.5 text-[11px] text-destructive'
          >
            {error}
          </p>
        ) : helpText ? (
          <p id={helpId} className='mt-1.5 text-[11px] text-muted-foreground'>
            {helpText}
          </p>
        ) : null}
      </div>
    )
  }
)

Input.displayName = 'Input'

export type SelectProps = FieldLabelProps &
  BaseFieldProps &
  Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> & {
    leadingIcon?: IconInput
  }

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      srLabel,
      helpText,
      error,
      inputSize = 'md',
      rounded = 'none',
      leadingIcon,
      id,
      containerClassName,
      className,
      children,
      ...selectProps
    },
    ref
  ) => {
    const reactId = React.useId()
    const fieldId = id ?? `select-${reactId}`
    const helpId = helpText ? `${fieldId}-help` : undefined
    const errorId = error ? `${fieldId}-error` : undefined
    const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined

    const padding = paddingMap[inputSize ?? 'md']
    const iconSize = iconSizeMap[inputSize ?? 'md']
    const iconWrappers = iconWrapperMap[inputSize ?? 'md']

    const paddingClass = cn(
      padding.base,
      leadingIcon && padding.leading,
      'pr-8'
    )

    return (
      <div className={cn('w-full', containerClassName)}>
        {label ? (
          <label
            htmlFor={fieldId}
            className='block text-xs font-medium text-foreground mb-1.5'
          >
            {label}
          </label>
        ) : (
          <label htmlFor={fieldId} className='sr-only'>
            {srLabel}
          </label>
        )}

        <div className='relative w-full'>
          {leadingIcon && (
            <span
              className={cn(
                'absolute top-1/2 -translate-y-1/2 flex items-center pointer-events-none',
                iconWrappers.leading
              )}
            >
              {renderIcon(leadingIcon, iconSize)}
            </span>
          )}
          <select
            ref={ref}
            id={fieldId}
            aria-invalid={!!error || undefined}
            aria-describedby={describedBy}
            className={cn(
              fieldVariants({ inputSize, rounded }),
              'w-full appearance-none cursor-pointer',
              paddingClass,
              className
            )}
            {...selectProps}
          >
            {children}
          </select>
          <span
            className={cn(
              'absolute top-1/2 -translate-y-1/2 flex items-center pointer-events-none text-muted-foreground',
              iconWrappers.trailing
            )}
            aria-hidden='true'
          >
            <svg
              width={iconSize}
              height={iconSize}
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='m6 9 6 6 6-6' />
            </svg>
          </span>
        </div>

        {error ? (
          <p
            id={errorId}
            role='alert'
            className='mt-1.5 text-[11px] text-destructive'
          >
            {error}
          </p>
        ) : helpText ? (
          <p id={helpId} className='mt-1.5 text-[11px] text-muted-foreground'>
            {helpText}
          </p>
        ) : null}
      </div>
    )
  }
)

Select.displayName = 'Select'
